// biome-ignore-all lint/a11y: TODO - address a11y issues later
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ChannelMetrics } from "@/hooks/api/use-channel-metrics";
import type { Channel } from "@/hooks/api/use-channels";
import { DEFAULT_PRIMARY_COLUMN_WIDTHS } from "@/lib/column-defaults";
import { getFilterType } from "@/lib/metric-types";
import { formatMetricValue } from "@/lib/metrics/metric-formatting";
import {
  METRIC_CONFIG,
  METRIC_DATA_TYPES,
} from "@/pages/channels/channel-explorer/channel-metrics-config";
import type { UnifiedTableColumn } from "@/types/table-column";

/**
 * Extended channel column type with category and style information
 */
export interface ChannelColumn extends UnifiedTableColumn<Channel> {
  category?: string;
  categoryStyle?: string;
}

export const getChannelColumns = (
  selectedMetrics?: Array<keyof ChannelMetrics>,
  onMetricClick?: (
    channelName: string,
    metric: keyof ChannelMetrics,
    metricValue?: number | string,
  ) => void,
): ChannelColumn[] => {
  // Base columns for channels (simplified compared to campaigns)
  const baseColumns = [
    {
      key: "name",
      title: "Channel Name",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: DEFAULT_PRIMARY_COLUMN_WIDTHS.LARGE,
      required: true, // Primary column that cannot be hidden
      render: (channel: Channel) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2 w-full">
              <Link
                to={`/channels/${encodeURIComponent(channel.name)}/explorer`}
                className="text-foreground p-4 rounded hover:bg-accent transition-colors block flex-1"
                title={channel.name}
              >
                {channel.name}
              </Link>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>View in Channel Explorer</p>
          </TooltipContent>
        </Tooltip>
      ),
    },
  ] as UnifiedTableColumn<Channel>[];

  // Add metric columns if selectedMetrics is provided
  if (selectedMetrics && selectedMetrics.length > 0) {
    const metricColumns = selectedMetrics.map((metricKey) => {
      const metricConfig = METRIC_CONFIG[metricKey];
      const dataType = METRIC_DATA_TYPES[metricKey];

      return {
        key: `metric_${metricKey}`,
        title: metricConfig.title,
        tooltip: metricConfig.tooltip,
        columnType: "metric",
        sortable: true,
        filterable: true,
        filterType: getFilterType(dataType),
        nullSort: "last",
        defaultSortDirection: "desc",
        render: (
          channel: Channel & {
            metrics?: Record<keyof ChannelMetrics, number | string | undefined>;
          },
        ) => {
          if (!channel.metrics) {
            return <Skeleton className="h-4 w-12" />;
          }

          const metricValue = channel.metrics?.[metricKey];
          if (metricValue === undefined || metricValue === null) {
            return <span className="text-muted-foreground">-</span>;
          }

          // Use data type from METRIC_DATA_TYPES for rendering
          const dataType = METRIC_DATA_TYPES[metricKey] ?? "default";

          const formattedContent = (
            <span className="font-medium">
              {formatMetricValue(metricValue, dataType)}
            </span>
          );

          // Check if this metric supports drill-down
          const supportsClickableDrilldown =
            metricConfig.drilldownColumns &&
            metricConfig.drilldownColumns.length > 0;

          const clickableContent =
            onMetricClick && supportsClickableDrilldown ? (
              <button
                className="w-full h-full text-left p-0 hover:underline decoration-2 underline-offset-4 transition-all cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onMetricClick(channel.name, metricKey, metricValue);
                }}
              >
                {formattedContent}
              </button>
            ) : (
              formattedContent
            );

          return clickableContent;
        },
        category: metricConfig.category,
        categoryStyle: CATEGORY_STYLES[metricConfig.category]?.color,
      } as UnifiedTableColumn<Channel>;
    });

    return [...baseColumns, ...metricColumns];
  }

  return baseColumns;
};

// Helper: convert UnifiedTableColumn list to TanStack ColumnDef list for useReactTable
export const getChannelColumnDefs = (
  selectedMetrics?: Array<keyof ChannelMetrics>,
  onMetricClick?: (
    channelName: string,
    metric: keyof ChannelMetrics,
    metricValue?: number | string,
  ) => void,
): ColumnDef<Channel>[] => {
  const unifiedColumns = getChannelColumns(selectedMetrics, onMetricClick);

  // Exclude columns that are for export only
  return unifiedColumns
    .filter((col) => !col.exportOnly)
    .map((col): ColumnDef<Channel> => {
      const colId = col.key.toString();

      // Basic cell renderer: use provided render or raw value
      const cellRenderer = col.render
        ? (info: { row: { original: Channel } }) =>
            typeof col.render === "function"
              ? col.render(info.row.original)
              : "-"
        : (info: { getValue: () => unknown }) => {
            const val = info.getValue();
            return val === null || val === undefined || val === ""
              ? "-"
              : (val as React.ReactNode);
          };

      // Use column size
      const size = col.size;

      return {
        id: colId,
        accessorKey: typeof col.key === "string" ? col.key : undefined,
        header: () => col.title,
        cell: cellRenderer,
        enableSorting: !!col.sortable,
        size,
        meta: {
          title: col.title, // Store the column title in meta for accessibility
          categoryStyle: col.categoryStyle,
          sortable: col.sortable,
          filterable: col.filterable,
          filterType: col.filterType,
          filterOptions: col.filterOptions,
          defaultSortDirection: col.defaultSortDirection,
          nullSort: col.nullSort,
          required: col.required, // Pass through the required flag
        },
      } as ColumnDef<Channel>;
    });
};
